import { useState, useEffect, useRef, useCallback } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement,
  Tooltip, Legend, Title,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { getDashboard, searchTransactions, getSuggestions } from '../api'
import { useMode } from '../context/ModeContext'
import { useBusinessContext } from '../context/BusinessContext'
import { snapshotPage, popPageSnapshot, restoreScroll } from '../utils/pageState'
import { PBBadge, CatPill } from '../components/txDisplay'
import type { Transaction, SearchResponse, SuggestionResult, ChartEntry } from '../types'

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend, Title)

// ── Constants ──────────────────────────────────────────────────────────────────
const PAGE_SIZE = 100

const money = (v: number) =>
  '$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

// ── Period / month types ───────────────────────────────────────────────────────
type Period   = 'last12' | 'all' | number
type MonthSel = { year: number; month: number } | null

interface MonthPill { year: number; month: number; label: string }

function toDateStr(d: Date) { return d.toISOString().slice(0, 10) }

function periodToRange(period: Period, month: MonthSel): { dateFrom: string; dateTo: string } {
  const today = new Date()
  if (period === 'all') {
    return {
      dateFrom: toDateStr(new Date(today.getFullYear() - 5, 0, 1)),
      dateTo:   toDateStr(today),
    }
  }
  if (month !== null) {
    const from    = new Date(month.year, month.month - 1, 1)
    const lastDay = new Date(month.year, month.month, 0)
    return { dateFrom: toDateStr(from), dateTo: toDateStr(lastDay) }
  }
  if (period === 'last12') {
    const twelve = new Date(today)
    twelve.setMonth(twelve.getMonth() - 12)
    return { dateFrom: toDateStr(twelve), dateTo: toDateStr(today) }
  }
  const year = period as number
  const from  = new Date(year, 0, 1)
  const to    = year === today.getFullYear() ? today : new Date(year, 11, 31)
  return { dateFrom: toDateStr(from), dateTo: toDateStr(to) }
}

function computeMonthPills(period: Period): MonthPill[] {
  if (period === 'all') return []
  const today = new Date()
  if (period === 'last12') {
    const pills: MonthPill[] = []
    for (let i = 12; i >= 0; i--) {
      const d  = new Date(today.getFullYear(), today.getMonth() - i, 1)
      const yr = d.getFullYear()
      const mo = d.getMonth() + 1
      pills.push({ year: yr, month: mo, label: MONTH_NAMES[mo - 1] + " '" + String(yr).slice(2) })
    }
    return pills
  }
  const year     = period as number
  const maxMonth = year === today.getFullYear() ? today.getMonth() + 1 : 12
  return Array.from({ length: maxMonth }, (_, i) => ({
    year, month: i + 1, label: MONTH_NAMES[i],
  }))
}

// ── Chart builder ──────────────────────────────────────────────────────────────
type ChartMode = 'month' | 'category' | 'tag'

interface ChartResult {
  labels: string[]
  datasets: unknown[]
  avgInc: number
  avgSpd: number
}

function buildChartData(
  mode: ChartMode,
  monthlyTotals: ChartEntry[],
  monthlyIncomeTotals: ChartEntry[],
  categoryTotals: ChartEntry[],
  tagTotals: ChartEntry[],
  totalEarned: number,
  totalSpent: number,
): ChartResult {
  const spendBg   = 'rgba(99,102,241,0.65)'
  const spendHov  = 'rgba(99,102,241,0.85)'
  const incomeBg  = 'rgba(16,185,129,0.55)'
  const incomeHov = 'rgba(16,185,129,0.75)'

  if (mode === 'month') {
    const labels  = monthlyTotals.map(m => m.label)
    const incMap  = Object.fromEntries(monthlyIncomeTotals.map(m => [m.label, m.total]))
    const incData = labels.map(l => incMap[l] ?? 0)
    const mc      = labels.length
    const avgInc  = mc > 0 ? totalEarned / mc : 0
    const avgSpd  = mc > 0 ? totalSpent  / mc : 0
    return {
      labels, avgInc, avgSpd,
      datasets: [
        { type: 'bar' as const, label: 'Spending', order: 2,
          data: monthlyTotals.map(m => m.total),
          backgroundColor: spendBg, hoverBackgroundColor: spendHov,
          borderRadius: 6, borderSkipped: false as const,
          barPercentage: 0.5, categoryPercentage: 0.75 },
        { type: 'bar' as const, label: 'Income', order: 2,
          data: incData,
          backgroundColor: incomeBg, hoverBackgroundColor: incomeHov,
          borderRadius: 6, borderSkipped: false as const,
          barPercentage: 0.5, categoryPercentage: 0.75 },
        { type: 'line' as const, label: 'Avg Income', order: 0,
          data: labels.map(() => avgInc),
          borderColor: 'rgba(16,185,129,0.85)', backgroundColor: 'transparent',
          borderWidth: 2, borderDash: [6, 4], pointRadius: 0, tension: 0,
          hidden: avgInc <= 0 },
        { type: 'line' as const, label: 'Avg Spending', order: 0,
          data: labels.map(() => avgSpd),
          borderColor: 'rgba(239,68,68,0.8)', backgroundColor: 'transparent',
          borderWidth: 2, borderDash: [6, 4], pointRadius: 0, tension: 0,
          hidden: avgSpd <= 0 },
      ],
    }
  }

  const entries = mode === 'category' ? categoryTotals : tagTotals
  return {
    labels: entries.map(e => e.label),
    avgInc: 0, avgSpd: 0,
    datasets: [
      { type: 'bar' as const, label: 'Amount', order: 2,
        data: entries.map(e => e.total),
        backgroundColor: spendBg, hoverBackgroundColor: spendHov,
        borderRadius: 6, borderSkipped: false as const,
        barPercentage: 0.6, categoryPercentage: 0.8 },
    ],
  }
}

// ── Search param builder ───────────────────────────────────────────────────────
interface ActiveFilter { field: string; value: string; label: string }

// Groups filters by field → comma-separated OR lists; AND across different fields.
// A 'month' filter overrides dateFrom/dateTo rather than adding a field match.
function buildSearchParams(
  filters: ActiveFilter[],
  q: string | null,
  dateFrom: string,
  dateTo: string,
): Record<string, string> {
  const params: Record<string, string> = {}

  // Month filter overrides the period date range
  const monthFilter = filters.find(f => f.field === 'month')
  if (monthFilter) {
    const [yr, mo] = monthFilter.value.split('-').map(Number)
    const from    = new Date(yr, mo - 1, 1)
    const lastDay = new Date(yr, mo, 0)
    params.dateFrom = toDateStr(from)
    params.dateTo   = toDateStr(lastDay)
  } else {
    params.dateFrom = dateFrom
    params.dateTo   = dateTo
  }

  // Group chip filters by field (OR within field, AND across fields); exclude 'month'
  const chipFilters = filters.filter(f => f.field !== 'month')
  const byField: Record<string, string[]> = {}
  for (const f of chipFilters) {
    if (!byField[f.field]) byField[f.field] = []
    byField[f.field].push(f.value)
  }

  if (byField['merchant'])    params.merchants     = byField['merchant'].join(',')
  if (byField['category'])    params.categories    = byField['category'].join(',')
  if (byField['subcategory']) params.subcategories = byField['subcategory'].join(',')
  if (byField['tag'])         params.tags          = byField['tag'].join(',')
  if (q)                      params.q             = q

  return params
}


// ── Sort types ─────────────────────────────────────────────────────────────────
type SortDir   = 'asc' | 'desc'

// ── Progressive multi-key sorting ───────────────────────────────────────────────
type SortableField = 'date' | 'account' | 'merchant' | 'category' | 'tags' | 'charges'
type SortKey = { field: SortableField; dir: SortDir }
type PBFilter = 'all' | 'P' | 'B'
// Default hierarchy: most-recent date, then merchant A–Z, then amount.
const DEFAULT_SORT: SortKey[] = [
  { field: 'date', dir: 'desc' }, { field: 'merchant', dir: 'asc' }, { field: 'charges', dir: 'asc' },
]
function sortValue(t: Transaction, f: SortableField): string | number {
  switch (f) {
    case 'date':     return t.date ?? ''
    case 'account':  return (t.account ?? '').toLowerCase()
    case 'merchant': return (t.merchantDisplayName ?? '').toLowerCase()
    case 'category': return (t.category ?? '').toLowerCase()
    case 'tags':     return (t.tag ?? '').toLowerCase()
    case 'charges':  return t.charges ?? 0
  }
}
function multiSort(items: Transaction[], keys: SortKey[]): Transaction[] {
  if (keys.length === 0) return items
  return [...items].sort((a, b) => {
    for (const k of keys) {
      const av = sortValue(a, k.field), bv = sortValue(b, k.field)
      const cmp = typeof av === 'string' ? av.localeCompare(bv as string) : (av as number) - (bv as number)
      if (cmp !== 0) return k.dir === 'asc' ? cmp : -cmp
    }
    return 0
  })
}

// ── Page-state type ────────────────────────────────────────────────────────────
interface DashboardPageState {
  period: Period; selectedMonth: MonthSel
  chartMode: ChartMode; sortKeys: SortKey[]; pbFilter: PBFilter; tablePage: number
  activeFilters: ActiveFilter[]; query: string
}

// ── Main component ─────────────────────────────────────────────────────────────
function DashSortTh({
  label, field, sortKeys, onSort, right,
}: {
  label: string; field: SortableField; sortKeys: SortKey[]
  onSort: () => void; right?: boolean
}) {
  const idx = sortKeys.findIndex(k => k.field === field)
  const active = idx >= 0
  const dir = active ? sortKeys[idx].dir : null
  return (
    <th
      onClick={onSort}
      style={{
        cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
        fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '0.04em', color: idx === 0 ? 'var(--pb-primary)' : active ? 'var(--pb-text)' : 'var(--pb-muted)',
        padding: '0.5rem 0.6rem', borderBottom: '2px solid var(--pb-border)',
        transition: 'color 0.15s', textAlign: right ? 'right' : 'left',
      }}
    >
      {label}
      {active && <i className={`bi ms-1 ${dir === 'asc' ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ fontSize: '0.65rem' }} />}
    </th>
  )
}

export default function Dashboard() {
  const navigate = useNavigate()
  const { mode } = useMode()
  const { selectedBusinessId } = useBusinessContext()
  const isPersonal   = mode === 'personal'
  const bizId        = mode === 'business' ? selectedBusinessId : null

  const [period, setPeriod]               = useState<Period>('last12')
  const [selectedMonth, setSelectedMonth] = useState<MonthSel>(null)

  const { dateFrom, dateTo } = periodToRange(period, selectedMonth)
  const monthPills            = computeMonthPills(period)

  const [sortKeys, setSortKeys] = useState<SortKey[]>(DEFAULT_SORT)
  const [pbFilter, setPbFilter] = useState<PBFilter>('all')
  const [tablePage, setTablePage] = useState(1)

  // getDashboard provides KPIs + chart aggregates for the period (its items are unused
  // by the table, which loads the full set separately for client-side sort/filter).
  const { data, error } = useQuery({
    queryKey: ['dashboard', dateFrom, dateTo, isPersonal, bizId],
    queryFn:  () => getDashboard(1, dateFrom, dateTo, null, 'asc', isPersonal, bizId),
  })

  // Search state
  const [query, setQuery]               = useState('')
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([])
  const [searchResults, setSearchResults] = useState<SearchResponse | null>(null)
  const [suggestions, setSuggestions]   = useState<SuggestionResult | null>(null)
  const [showSug, setShowSug]           = useState(false)
  const [sugActiveIdx, setSugActiveIdx] = useState(-1)

  const [chartMode, setChartMode] = useState<ChartMode>('month')
  const [pendingScroll, setPendingScroll] = useState<number | null>(null)

  // Full period transaction set for the table (date range + any active filters/query),
  // so sorting / P-B filtering / pagination all run client-side across the whole set.
  const { data: tableData, isFetching: tableFetching } = useQuery({
    queryKey: ['dash-table', dateFrom, dateTo, isPersonal, bizId, JSON.stringify(activeFilters), query.trim()],
    queryFn:  () => searchTransactions({ ...buildSearchParams(activeFilters, query.trim() || null, dateFrom, dateTo), isPersonal, businessId: bizId ?? undefined }),
  })

  const searchWrapRef  = useRef<HTMLDivElement>(null)
  const debounceRef    = useRef<ReturnType<typeof setTimeout>>()
  const searchSeqRef   = useRef(0)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const chartClickRef  = useRef<(event: any, elements: any[]) => void>(() => {})

  // ── Search core (defined before restore so it can be referenced in the effect) ──
  const doSearch = useCallback(async (
    filters: ActiveFilter[],
    q: string,
    dfrom: string,
    dto: string,
  ) => {
    const seq    = ++searchSeqRef.current
    const params = buildSearchParams(filters, q.trim() || null, dfrom, dto)
    setTablePage(1)
    if (filters.length === 0 && !q.trim()) {
      if (seq === searchSeqRef.current) setSearchResults(null)
      return
    }
    const result = await searchTransactions({ ...params, isPersonal, businessId: bizId ?? undefined })
    if (seq !== searchSeqRef.current) return
    setSearchResults(result)
  }, [])

  // Restore state when returning from edit
  useEffect(() => {
    const snap = popPageSnapshot<DashboardPageState>('dashboard')
    if (!snap) return
    const s = snap.state
    setPeriod(s.period)
    setSelectedMonth(s.selectedMonth)
    setChartMode(s.chartMode)
    setSortKeys(s.sortKeys ?? DEFAULT_SORT)
    setPbFilter(s.pbFilter ?? 'all')
    setTablePage(s.tablePage ?? 1)
    const restoredFilters = s.activeFilters ?? []
    const restoredQuery   = s.query ?? ''
    setActiveFilters(restoredFilters)
    setQuery(restoredQuery)
    setPendingScroll(snap.scroll)
    // Re-run search with the correct date range computed from restored period/month
    if (restoredFilters.length > 0 || restoredQuery.trim()) {
      const { dateFrom: df, dateTo: dt } = periodToRange(s.period, s.selectedMonth)
      doSearch(restoredFilters, restoredQuery, df, dt)
    }
  }, [doSearch])

  // Scroll to saved position after snapshot restore
  useEffect(() => {
    if (pendingScroll === null) return
    setPendingScroll(null)
    restoreScroll(pendingScroll)
  }, [pendingScroll])

  // ── Derived display ──────────────────────────────────────────────────────────
  const isSearchActive      = searchResults !== null
  const displayTotalSpent   = isSearchActive ? searchResults.totalSpent          : (data?.totalSpent          ?? 0)
  const displayTotalEarned  = isSearchActive ? searchResults.totalEarned         : (data?.totalEarned         ?? 0)
  const displayTotal        = isSearchActive ? searchResults.count               : (data?.total               ?? 0)
  const displayMonthly      = isSearchActive ? searchResults.monthlyTotals       : (data?.monthlyTotals       ?? [])
  const displayMonthlyInc   = isSearchActive ? searchResults.monthlyIncomeTotals : (data?.monthlyIncomeTotals ?? [])
  const displayCategoryTots = isSearchActive ? searchResults.categoryTotals      : (data?.categoryTotals      ?? [])
  const displayTagTots      = isSearchActive ? searchResults.tagTotals           : (data?.tagTotals           ?? [])

  // ── Table data: full set → P/B filter → multi-key sort → client pagination ──────
  const pbMatch = (t: Transaction): boolean => {
    if (pbFilter === 'all') return true
    const bizIds = (t.isSplit && t.splitLines?.length)
      ? t.splitLines.map(l => l.businessId ?? null)
      : [t.primaryLineBusinessId ?? null]
    return pbFilter === 'P' ? bizIds.some(b => b == null) : bizIds.some(b => b != null)
  }
  const tableSorted = multiSort((tableData?.items ?? []).filter(pbMatch), sortKeys)
  const tableCount        = tableSorted.length
  const currentTotalPages = Math.max(1, Math.ceil(tableCount / PAGE_SIZE))
  const currentPage       = Math.min(tablePage, currentTotalPages)
  const currentItems: Transaction[] = tableSorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  // ── Chart ────────────────────────────────────────────────────────────────────
  const chartResult = buildChartData(
    chartMode, displayMonthly, displayMonthlyInc,
    displayCategoryTots, displayTagTots,
    displayTotalEarned, displayTotalSpent,
  )
  const { avgInc, avgSpd } = chartResult

  // ── Sort header — progressive multi-key: promote clicked column to the front ────
  const onSortHeader = (field: SortableField) => {
    setSortKeys(prev => {
      if (prev[0]?.field === field) {
        // Re-clicking the primary column toggles its direction.
        return [{ field, dir: prev[0].dir === 'asc' ? 'desc' : 'asc' }, ...prev.slice(1)]
      }
      const rest = prev.filter(k => k.field !== field)
      return [{ field, dir: field === 'date' ? 'desc' : 'asc' }, ...rest]
    })
    setTablePage(1)
  }

  const fetchSuggestions = useCallback(async (
    q: string,
    filters: ActiveFilter[],
    dfrom: string,
    dto: string,
  ) => {
    if (q.length < 1) { setShowSug(false); return }
    const fp = buildSearchParams(filters, null, dfrom, dto)
    const result = await getSuggestions(q, {
      merchants:     fp.merchants,
      categories:    fp.categories,
      subcategories: fp.subcategories,
      tags:          fp.tags,
      dateFrom:      fp.dateFrom,
      dateTo:        fp.dateTo,
      isPersonal:    String(isPersonal),
      businessId:    bizId != null ? String(bizId) : undefined,
    })
    setSuggestions(result)
    setShowSug(true)
    setSugActiveIdx(-1)
  }, [])

  const onQueryChange = (q: string) => {
    setQuery(q)
    clearTimeout(debounceRef.current)
    if (!q.trim()) {
      setShowSug(false)
      doSearch(activeFilters, '', dateFrom, dateTo)
      return
    }
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(q, activeFilters, dateFrom, dateTo)
      doSearch(activeFilters, q, dateFrom, dateTo)
    }, 280)
  }

  const applyFilterMulti = useCallback((field: string, value: string, label: string) => {
    clearTimeout(debounceRef.current)
    setActiveFilters(prev => {
      if (prev.some(f => f.field === field && f.value === value)) return prev
      const next = [...prev, { field, value, label }]
      doSearch(next, '', dateFrom, dateTo)
      return next
    })
    setQuery('')
    setShowSug(false)
  }, [doSearch, dateFrom, dateTo])

  const applyMonthFilter = useCallback((pill: MonthPill) => {
    clearTimeout(debounceRef.current)
    const value = `${pill.year}-${String(pill.month).padStart(2, '0')}`
    const label = 'Month: ' + pill.label
    setActiveFilters(prev => {
      const withoutMonth = prev.filter(f => f.field !== 'month')
      const next = [...withoutMonth, { field: 'month', value, label }]
      doSearch(next, '', dateFrom, dateTo)
      return next
    })
    setQuery('')
    setShowSug(false)
  }, [doSearch, dateFrom, dateTo])

  // Flatten suggestions for keyboard navigation
  const flat: { field: string; value: string; group: string }[] = []
  if (suggestions) {
    suggestions.merchants.forEach(v    => flat.push({ field: 'merchant',    value: v, group: 'Merchant' }))
    suggestions.categories.forEach(v   => flat.push({ field: 'category',    value: v, group: 'Category' }))
    suggestions.subCategories.forEach(v => flat.push({ field: 'subcategory', value: v, group: 'Subcategory' }))
    suggestions.tags.forEach(v         => flat.push({ field: 'tag',          value: v, group: 'Tag' }))
  }

  const onKeyDownSearch = (e: ReactKeyboardEvent) => {
    if (!showSug || flat.length === 0) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setSugActiveIdx(i => Math.min(i + 1, flat.length - 1)) }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setSugActiveIdx(i => Math.max(i - 1, -1)) }
    if (e.key === 'Escape')    { setShowSug(false) }
    if (e.key === 'Enter') {
      if (sugActiveIdx >= 0 && flat[sugActiveIdx]) {
        const { field, value, group } = flat[sugActiveIdx]
        applyFilterMulti(field, value, group + ': ' + value)
      } else {
        setShowSug(false)
        if (query.trim()) doSearch(activeFilters, query.trim(), dateFrom, dateTo)
      }
    }
  }

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!searchWrapRef.current?.contains(e.target as Node)) setShowSug(false)
    }
    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [])

  const goToEdit = (id: string) => {
    snapshotPage<DashboardPageState>('dashboard', {
      period, selectedMonth, chartMode, sortKeys, pbFilter, tablePage,
      activeFilters, query,
    })
    navigate(`/transactions/${id}/edit`)
  }

  const changePeriod = (p: Period) => {
    setPeriod(p); setSelectedMonth(null); setTablePage(1)
    setSearchResults(null); setActiveFilters([]); setQuery('')
  }
  const changeMonth = (m: MonthSel) => {
    setSelectedMonth(m); setTablePage(1)
    setSearchResults(null); setActiveFilters([]); setQuery('')
  }

  const goPage = (dir: number) => {
    setTablePage(p => Math.max(1, Math.min(currentTotalPages, p + dir)))
  }

  // ── Chart click ──────────────────────────────────────────────────────────────
  chartClickRef.current = (_event: unknown, elements: { index: number; datasetIndex?: number }[]) => {
    if (!elements.length) return
    const { index: idx } = elements[0]
    const label = (chartResult.labels as string[])[idx]
    if (!label) return
    if (chartMode === 'month') {
      const parts = label.split(' ')
      if (parts.length === 2) {
        const moIdx = MONTH_NAMES.indexOf(parts[0])
        const yr    = parseInt(parts[1], 10)
        if (moIdx >= 0 && yr > 0) {
          applyMonthFilter({ year: yr, month: moIdx + 1, label: `${parts[0]} '${parts[1].slice(2)}` })
        }
      }
      return
    }
    if (chartMode === 'category') { applyFilterMulti('category', label, 'Category: ' + label); return }
    if (chartMode === 'tag')      { applyFilterMulti('tag',      label, 'Tag: '      + label) }
  }

  // ── Pager values ─────────────────────────────────────────────────────────────
  const pagerStart = (currentPage - 1) * PAGE_SIZE + 1
  const pagerEnd   = Math.min(currentPage * PAGE_SIZE, tableCount)

  // ── Period button list ───────────────────────────────────────────────────────
  const thisYear = new Date().getFullYear()
  const earliest = data?.earliestYear ?? (thisYear - 3)
  const minYear   = Math.max(earliest, thisYear - 5)
  const yearRange: number[] = []
  for (let y = thisYear; y >= minYear; y--) yearRange.push(y)
  const periodBtns: { label: string; value: Period }[] = [
    { label: 'Last 12 Mo', value: 'last12' },
    ...yearRange.map(y => ({ label: String(y), value: y as Period })),
    { label: 'All', value: 'all' },
  ]

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div>
      <h1 className="pb-page-title mb-3">Transactions</h1>

      {/* Period selector */}
      <div className="mb-3">
        <div className="pb-chart-selector" style={{ display: 'inline-flex' }}>
          {periodBtns.map(b => (
            <button key={String(b.value)}
              className={`pb-chart-sel-btn${period === b.value ? ' active' : ''}`}
              onClick={() => changePeriod(b.value)}>
              {b.label}
            </button>
          ))}
        </div>
      </div>

      {/* Month pills */}
      {monthPills.length > 0 && (
        <div className="pb-month-pills mb-4">
          <button
            className={`pb-month-pill${selectedMonth === null ? ' active' : ''}`}
            onClick={() => changeMonth(null)}
          >
            All
          </button>
          {monthPills.map(p => {
            const isActive = selectedMonth?.year === p.year && selectedMonth?.month === p.month
            return (
              <button
                key={`${p.year}-${p.month}`}
                className={`pb-month-pill${isActive ? ' active' : ''}`}
                onClick={() => changeMonth({ year: p.year, month: p.month })}
              >
                {p.label}
              </button>
            )
          })}
        </div>
      )}

      {/* KPI row */}
      <div className="row g-3 mb-4">
        <div className="col-sm-6 col-xl-3">
          <div className="pb-card pb-card-sm h-100">
            <div className="pb-kpi-label mb-1"><i className="bi bi-arrow-up-circle me-1" />Total Spent</div>
            <div className="pb-kpi-value">{money(displayTotalSpent)}</div>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="pb-card pb-card-sm h-100">
            <div className="pb-kpi-label mb-1"><i className="bi bi-arrow-down-circle me-1" />Total Earned</div>
            <div className="pb-kpi-value" style={{ color: '#16a34a' }}>{money(displayTotalEarned)}</div>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="pb-card pb-card-sm h-100">
            <div className="pb-kpi-label mb-1"><i className="bi bi-calculator me-1" />NET</div>
            <div className="pb-kpi-value" style={{ color: (displayTotalEarned - displayTotalSpent) >= 0 ? '#16a34a' : 'var(--pb-danger, #ef4444)' }}>
              {(displayTotalEarned - displayTotalSpent) >= 0 ? '+' : ''}{money(displayTotalEarned - displayTotalSpent)}
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="pb-card pb-card-sm h-100">
            <div className="pb-kpi-label mb-1"><i className="bi bi-receipt-cutoff me-1" />Transactions</div>
            <div className="pb-kpi-value">{displayTotal.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="pb-card mb-4">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <div className="pb-kpi-label">
              <i className="bi bi-bar-chart-line me-2" style={{ color: 'var(--pb-muted)' }} />Spending
            </div>
            {chartMode === 'month' && avgInc > 0 && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.72rem', fontWeight: 600, color: '#059669' }}>
                <svg width="22" height="10" aria-hidden="true"><line x1="0" y1="5" x2="22" y2="5" stroke="rgba(16,185,129,0.85)" strokeWidth="2" strokeDasharray="5,3"/></svg>
                Avg income: {money(avgInc)}/mo
              </span>
            )}
            {chartMode === 'month' && avgSpd > 0 && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.72rem', fontWeight: 600, color: '#dc2626' }}>
                <svg width="22" height="10" aria-hidden="true"><line x1="0" y1="5" x2="22" y2="5" stroke="rgba(239,68,68,0.8)" strokeWidth="2" strokeDasharray="5,3"/></svg>
                Avg spending: {money(avgSpd)}/mo
              </span>
            )}
          </div>
          <div className="pb-chart-selector">
            {(['month', 'category', 'tag'] as ChartMode[]).map(m => (
              <button key={m}
                className={`pb-chart-sel-btn${chartMode === m ? ' active' : ''}`}
                onClick={() => setChartMode(m)}>
                {m.charAt(0).toUpperCase() + m.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <div style={{ position: 'relative', height: 220 }}>
          <Bar
            data={chartResult as never}
            options={{
              responsive: true, maintainAspectRatio: false,
              plugins: { legend: { display: false } },
              scales: {
                x: { grid: { display: false }, ticks: { font: { size: 11 } } },
                y: {
                  grid: { color: 'rgba(0,0,0,0.06)' },
                  ticks: { callback: (v) => '$' + Number(v).toLocaleString() },
                },
              },
              onClick: (_e, elements) => chartClickRef.current(_e, elements),
            }}
          />
        </div>
      </div>

      {/* Search + filter chips */}
      <div className="mb-4">
        {/* Search row */}
        <div ref={searchWrapRef} className="pb-search-wrap">
          <div className="pb-search-box">
            <i className="bi bi-search pb-search-icon" />
            <input
              className="pb-search-input"
              placeholder="Search merchant, category, subcategory or tag…"
              value={query}
              onChange={e => onQueryChange(e.target.value)}
              onKeyDown={onKeyDownSearch}
              autoComplete="off"
              spellCheck={false}
            />
            <button
              className={`pb-search-clear${(query || activeFilters.length > 0) ? ' visible' : ''}`}
              title="Clear search"
              onClick={() => {
                clearTimeout(debounceRef.current)
                setActiveFilters([])
                setQuery('')
                setShowSug(false)
                setSearchResults(null)
                setTablePage(1)
              }}
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>

          {/* Suggestions dropdown */}
          {showSug && suggestions && flat.length > 0 && (
            <div className="pb-suggestions visible">
              {(['Merchant', 'Category', 'Subcategory', 'Tag'] as const).map(group => {
                const field = group.toLowerCase() === 'subcategory' ? 'subcategory' : group.toLowerCase()
                const items = group === 'Merchant'     ? suggestions.merchants
                            : group === 'Category'    ? suggestions.categories
                            : group === 'Subcategory' ? suggestions.subCategories
                            :                           suggestions.tags
                if (!items.length) return null
                return (
                  <div key={group}>
                    <div className="pb-sug-group-label">{group}</div>
                    {items.map(v => {
                      const fi = flat.findIndex(x => x.field === field && x.value === v)
                      return (
                        <div
                          key={v}
                          className={`pb-sug-item${fi === sugActiveIdx ? ' pb-sug-active' : ''}`}
                          onMouseDown={() => applyFilterMulti(field, v, group + ': ' + v)}
                        >
                          {v}
                        </div>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Active filter chips — below the search box */}
        {activeFilters.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.65rem' }}>
            {activeFilters.map(f => (
              <span key={f.field + f.value} className="pb-filter-chip">
                <i className="bi bi-funnel-fill" style={{ fontSize: '0.7rem' }} />
                {f.label}
                <button
                  className="pb-filter-chip-clear"
                  onClick={() => {
                    const next = activeFilters.filter(x => !(x.field === f.field && x.value === f.value))
                    setActiveFilters(next)
                    doSearch(next, query, dateFrom, dateTo)
                  }}
                >
                  <i className="bi bi-x" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Transactions table */}
      <div className="pb-card">
        {/* Header: count + Personal/Business filter */}
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div className="pb-kpi-label">
            <i className="bi bi-list-ul me-1" />Transactions
            <span style={{ marginLeft: 6, fontWeight: 400, color: 'var(--pb-muted)', fontSize: '0.82rem' }}>({tableCount.toLocaleString()})</span>
          </div>
          <div className="pb-chart-selector" style={{ display: 'inline-flex' }}>
            {(['all', 'P', 'B'] as PBFilter[]).map(v => (
              <button key={v}
                className={`pb-chart-sel-btn${pbFilter === v ? ' active' : ''}`}
                onClick={() => { setPbFilter(v); setTablePage(1) }}>
                {v === 'all' ? 'All' : v}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="pb-empty"><i className="bi bi-exclamation-triangle me-2" />Failed to load transactions.</div>
        )}
        {tableFetching && (
          <div style={{ padding: '0.5rem 0.6rem', fontSize: '0.8rem', color: 'var(--pb-muted)' }}>
            <span className="spinner-border spinner-border-sm me-2" style={{ width: 12, height: 12 }} />Loading…
          </div>
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr>
                <DashSortTh label="Date"     field="date"     sortKeys={sortKeys} onSort={() => onSortHeader('date')} />
                <DashSortTh label="Account"  field="account"  sortKeys={sortKeys} onSort={() => onSortHeader('account')} />
                <DashSortTh label="Merchant" field="merchant" sortKeys={sortKeys} onSort={() => onSortHeader('merchant')} />
                <DashSortTh label="Category" field="category" sortKeys={sortKeys} onSort={() => onSortHeader('category')} />
                <DashSortTh label="Tags"     field="tags"     sortKeys={sortKeys} onSort={() => onSortHeader('tags')} />
                <DashSortTh label="Amount"   field="charges"  sortKeys={sortKeys} onSort={() => onSortHeader('charges')} right />
                <th style={{ padding: '0.5rem 0.6rem', borderBottom: '2px solid var(--pb-border)', width: 36 }} />
              </tr>
            </thead>
            <tbody>
              {currentItems.length === 0 && !tableFetching && (
                <tr>
                  <td colSpan={7} style={{ padding: '2.55rem 0.6rem', borderBottom: '1px solid var(--pb-border)', textAlign: 'center', color: 'var(--pb-muted)' }}>
                    <i className="bi bi-inbox me-2" />No transactions found.
                  </td>
                </tr>
              )}
              {currentItems.map(t => {
                const isIncome = (t.charges ?? 0) < 0   // negative = money in → green "+"
                const tags = t.tag ? t.tag.split(', ').filter(Boolean) : []
                return (
                  <tr
                    key={t.id}
                    onClick={() => goToEdit(t.id)}
                    className="pb-table-row"
                    style={{ cursor: 'pointer' }}
                  >
                    {/* Date */}
                    <td style={{ padding: '0.55rem 0.6rem', borderBottom: '1px solid var(--pb-border)', color: 'var(--pb-muted)', whiteSpace: 'nowrap', fontSize: '0.78rem', verticalAlign: 'top' }}>{t.date ?? '—'}</td>
                    {/* Account */}
                    <td style={{ padding: '0.55rem 0.6rem', borderBottom: '1px solid var(--pb-border)', color: 'var(--pb-muted)', fontSize: '0.78rem', verticalAlign: 'top', whiteSpace: 'nowrap' }}>{t.account ?? '—'}</td>
                    {/* Merchant + inline pending badge */}
                    <td style={{ padding: '0.55rem 0.6rem', borderBottom: '1px solid var(--pb-border)', maxWidth: 200, verticalAlign: 'top' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, overflow: 'hidden' }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500, flex: 1 }}>{t.merchantDisplayName ?? <span style={{ color: 'var(--pb-muted)' }}>—</span>}</span>
                        {t.isPending && <span style={{ flexShrink: 0, fontSize: '0.62rem', fontWeight: 700, padding: '0.05rem 0.35rem', borderRadius: 4, background: 'rgba(234,179,8,0.12)', color: '#b45309', border: '1px solid rgba(234,179,8,0.3)', letterSpacing: '0.03em' }}>pending</span>}
                      </div>
                    </td>
                    {/* Category + Tags — split rows use one full-width card per line (colSpan 2) to stay aligned */}
                    {(() => { const isSplit = !!(t.isSplit && t.splitLines && t.splitLines.length > 0); return isSplit ? (
                    <td colSpan={2} style={{ padding: '0.55rem 0.6rem', borderBottom: '1px solid var(--pb-border)', verticalAlign: 'top' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {t.splitLines!.map((line, li) => (
                          <div key={li} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(148,163,184,0.08)', border: '1px solid var(--pb-border)', borderRadius: 6, padding: '0.28rem 0.6rem', minHeight: '1.65rem' }}>
                            <PBBadge businessId={line.businessId} />
                            <CatPill category={line.category} subCategory={line.subCategory} />
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.2rem', flex: 1, justifyContent: 'flex-end' }}>
                              {line.tags && line.tags.length > 0
                                ? line.tags.map(tg => <span key={tg} style={{ display: 'inline-block', fontSize: '0.64rem', fontWeight: 600, padding: '0.05rem 0.3rem', borderRadius: 4, background: 'rgba(99,102,241,0.1)', color: 'var(--pb-primary)', whiteSpace: 'nowrap' }}>{tg}</span>)
                                : null}
                            </div>
                            <span style={{ fontSize: '0.78rem', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0, minWidth: 70, textAlign: 'right' }}>{money(line.amount)}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                    ) : (
                    <>
                    <td style={{ padding: '0.55rem 0.6rem', borderBottom: '1px solid var(--pb-border)', verticalAlign: 'top' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                        <PBBadge businessId={t.primaryLineBusinessId} />
                        <CatPill category={t.category} subCategory={t.subCategory} />
                      </span>
                    </td>
                    <td style={{ padding: '0.55rem 0.6rem', borderBottom: '1px solid var(--pb-border)', verticalAlign: 'top' }}>
                      {tags.length > 0
                        ? tags.map(tg => (
                            <span key={tg} style={{
                              display: 'inline-block', fontSize: '0.68rem', fontWeight: 600,
                              padding: '0.1rem 0.45rem', borderRadius: 6, marginRight: '0.2rem',
                              background: 'rgba(99,102,241,0.1)', color: 'var(--pb-primary)',
                            }}>{tg}</span>
                          ))
                        : <span style={{ color: 'var(--pb-muted)' }}>—</span>}
                    </td>
                    </>
                    )})()}
                    {/* Amount */}
                    <td style={{ padding: '0.55rem 0.6rem', borderBottom: '1px solid var(--pb-border)', textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap', color: isIncome ? '#16a34a' : 'inherit', fontVariantNumeric: 'tabular-nums', verticalAlign: 'top' }}>
                      {t.charges != null ? ((isIncome ? '+' : '') + money(t.charges)) : '—'}
                    </td>
                    <td style={{ padding: '0.55rem 0.6rem', borderBottom: '1px solid var(--pb-border)', textAlign: 'right', width: 36, paddingLeft: 0 }}>
                      <i className="bi bi-chevron-right pb-row-chev" />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {currentTotalPages > 1 && (
          <div className="pb-pager">
            <div className="pb-pager-info">
              Showing <strong>{pagerStart}&ndash;{pagerEnd}</strong> of <strong>{tableCount.toLocaleString()}</strong>
            </div>
            <div className="pb-pager-controls">
              <button className="pb-pager-btn" disabled={currentPage <= 1} onClick={() => goPage(-1)}>
                <i className="bi bi-chevron-left" /> Prev
              </button>
              <span className="pb-pager-current">Page {currentPage} of {currentTotalPages}</span>
              <button className="pb-pager-btn" disabled={currentPage >= currentTotalPages} onClick={() => goPage(1)}>
                Next <i className="bi bi-chevron-right" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
